import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ProductModal } from "../ProductModal";
import "./ProductsByCategory.css";
import { ProductCard } from "../ProductCard/ProductCard";
import SearchSection from "../SearchBar/SearchSection";
import useIsMobile from "../../hooks/useIsMobile";
import { Pagination } from "./Pagination";
import FilterModal from "../FilterModal/FilterModal";
import { dummyBlogs } from '../../data/dummyblogs';
import ReactMarkdown from 'react-markdown';
import { baseUrl } from '../../utils/config';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from "@mui/material";
import axios from 'axios';

const Category = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]); // Raw products from backend
  const [filteredProducts, setFilteredProducts] = useState([]); // Products after filtering
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0); // <-- NEW: total from backend
  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12); // Show 12 items per page (3 rows of 4)
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({
    priceRange: [0, 1000],
    category: ''
  });

  // Create axios instance with token interceptor
  const api = axios.create({ baseURL: baseUrl });
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const categoryMap = {
    1: "Sport Wellness",
    2: "Beauty & Cosmetics",
    3: "OTC Medicine",
    4: "Special Offers",
    5: "Vitamins",
    6: "Skin Care",
    7: "Women care",
    8: "Hygiene care",
    9: "Baby care"
  };
  const categoryName = categoryMap[category] || 'Unknown';

  // Add state and helpers for blogs
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [blogsLoaded, setBlogsLoaded] = useState(false);

  useEffect(() => {
    // Reset scroll only once, before rendering anything meaningful
    window.scrollTo(0, 0);
  }, []);

  // Add this useEffect to scroll to top when page changes
  useEffect(() => {
    window.scrollTo({
      top: 280,
      behavior: 'smooth' // Optional: adds smooth scrolling animation
    });
  }, [currentPage]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Calculate skip for backend pagination
        const skip = (currentPage - 1) * itemsPerPage;
        // Build endpoint with pagination
        const url = `/products/categorie/${category}?skip=${skip}&limit=${itemsPerPage}`;
        console.log('Fetching products from:', url);
        const response = await api.get(url);
        const data = response.data;
        console.log('API response:', data);
        // Backend now returns {products: [], total: number, skip: number, limit: number}
        let productsArray = [];
        let total = 0;
        
        if (data && Array.isArray(data.products)) {
          productsArray = data.products;
          total = data.total || data.products.length;
        } else if (Array.isArray(data)) {
          // Fallback for old format
          productsArray = data;
          total = data.length;
        } else if (data && Array.isArray(data.data)) {
          productsArray = data.data;
          total = data.total || data.data.length;
        } else {
          console.warn('Could not extract products array from API response:', data);
        }
        console.log('productsArray:', productsArray);
        console.log('Total products from backend:', total);
        const formattedProducts = productsArray.map(item => {
          // Try all possible image fields
          let imageData = item.image_data || item.imageData || item.image || (item.images && item.images[0]?.image_url) || (item.images && item.images[0]?.image_base64) || "";
          console.log('[ProductsByCategory] Product:', item.name, 'Chosen imageData:', imageData);
          return {
            brand: item.brand || item.name,
            name: item.name,
            description: item.description || "No description available",
            price: item.price,
            imageData,
            id: item.id,
            partner_id: item.partner_info?.id || item.partner_id,
            partner_info: item.partner_info || {},
            inStock: item.inStock !== undefined
              ? (item.inStock === true || item.inStock === 'true')
              : true // default to true if missing
          };
        });
        console.log('formattedProducts:', formattedProducts);
        setProducts(formattedProducts);
        setFilteredProducts(formattedProducts); // Always sync filteredProducts with fetched
        setTotalProducts(total);
      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
        setFilteredProducts([]);
        setTotalProducts(0);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, currentPage, itemsPerPage]);

  useEffect(() => {
    try {
      const sortedBlogs = [...dummyBlogs]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 3);
      setRecentBlogs(sortedBlogs);
      setBlogsLoaded(true);
    } catch (error) {
      console.error('Error loading blogs:', error);
      setBlogsLoaded(true);
    }
  }, []);

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  const handleAddToCart = (details) => {
    console.log("Add to Cart:", {
      ...selectedProduct,
      ...details
    });
    setSelectedProduct(null); // Close modal after adding to cart
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    // Filter the products based on the new filters
    const filtered = products.filter(medicament => {
      const price = parseFloat(medicament.price);
      const inPriceRange = price >= newFilters.priceRange[0] && price <= newFilters.priceRange[1];
      const inCategory = !newFilters.category || medicament.category === newFilters.category;
      return inPriceRange && inCategory;
    });
    console.log('Filtered products:', filtered);
    setFilteredProducts(filtered);
    setCurrentPage(1); // Reset to first page when filters are applied
  };

  const isAuthenticated = () => {
    return !!localStorage.getItem("email"); // or whatever auth logic you use
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getFirstSentence = (content) => {
    const lines = content.split('\n').map(line => line.trim()).filter(Boolean);
    const isIntroduction = lines[0]?.toLowerCase().includes('introduction');
    const contentLine = isIntroduction ? (lines[1] || '') : (lines[0] || '');
    const words = contentLine.split(' ').slice(0, 20).join(' ');
    return words + '...';
  };

  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  useEffect(() => {
    console.log("showPartnerDialog:", showPartnerDialog);
  }, [showPartnerDialog]);

  const handleDialogClose = () => {
    setShowPartnerDialog(false);
  };

  if (loading) return <p>Loading products...</p>;

  return (
    <div className="container">
      <div className="wrapper">
        <h1 className="title"  style={{
            fontFamily : 'Playfair Display',
          }} >{categoryName} Products</h1>

        <div className="secondSection">
          <SearchSection
            medicaments={filteredProducts}
            isMobile={isMobile}
            onProductSelect={handleOpenModal}
            onOpenFilterModal={() => setShowFilterModal(true)}
            filterModalOpen={showFilterModal}
            onApplyFilters={handleApplyFilters}
            filteredMedicaments={filteredProducts}
            activeFilters={filters}
          />
        </div>

        <div className="productsGrid">
          {loading ? (
            <p>Loading products...</p>
          ) : filteredProducts.length > 0 ? (
            <>
              {/* Paginated Products */}
              <div className="gridContainer">
                {filteredProducts.map((product, index) => (
                  <div key={index} className="gridColumn" onClick={() => handleOpenModal(product)}>
                    <ProductCard {...product} setShowPartnerDialog={setShowPartnerDialog} />
                  </div>
                ))}
              </div>
              {/* Pagination Controls */}
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalProducts / itemsPerPage)}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </>
          ) : (
            <p>No products found for this category.</p>
          )}
        </div>

        {/* We guarantee high-quality service - same design as /Epharmacy */}
        <h2 className="serviceTitle"  style={{
            
            fontFamily : 'Playfair Display',
          }}>We guarantee high-quality service</h2>
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            padding: '24px',
            position: 'relative',
            display: 'grid',
            gap: '60px',
            
          }}
        >
          <div
            className="qualities-row"
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '24px',
              marginTop: '20px',
            }}
          >
            <div className="responsive-container">
              <img
                src="/external/vector1532-jyv3.svg"
                alt="Fast delivery"
                className="qualityImageFastDrive-style"
              />
              <span className="qualityMainText">Fast delivery</span>
              <span
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 400,
                  fontSize: '18px',
                  lineHeight: '60px',
                  color: '#020742',
                  textAlign: 'center',
                }}
              >
                Home delivery within 48 to 72 hours
              </span>
            </div>

            <div className="responsive-container">
              <img
                src="/external/vector1532-xri.svg"
                alt="Secure payment"
                className="qualityImageSecurePayment-style"
              />
              <span className="qualityMainText">
                {isMobile ? 'secure payment' : '100% secure payment'}
              </span>
              <span
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 400,
                  fontSize: '18px',
                  lineHeight: '60px',
                  color: '#020742',
                  textAlign: 'center',
                }}
              >
                Payment on delivery
              </span>
            </div>

            <div className="responsive-container">
              <img
                src="/external/star31532-8xfi.svg"
                alt="Free delivery"
                className="qualityImageFreeDelivery-style"
              />
              <span className="qualityMainText">Free delivery</span>
              <span
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 400,
                  fontSize: '18px',
                  lineHeight: '60px',
                  color: '#020742',
                  textAlign: 'center',
                }}
              >
                From 190 DT of purchases
              </span>
            </div>
          </div>
        </div>

        {selectedProduct && (
          <ProductModal
            onClose={handleCloseModal}
            onAddToCart={handleAddToCart}
            imageData={selectedProduct.imageData}
            brand={selectedProduct.brand}
            name={selectedProduct.name}
            subtitle={selectedProduct.variant}
            description={selectedProduct.description || "No description available"}
            price={selectedProduct.price}
            id={selectedProduct.id}
            partner_id={selectedProduct.partner_info?.id || selectedProduct.partner_id}
            partner_info={selectedProduct.partner_info}
            inStock={selectedProduct.inStock}
            setShowPartnerDialog={setShowPartnerDialog}
          />
        )}
        {/*  blogs section */}
        {/* blogs  */}
               <style>{`
                 /* Exact Healix blog section styles */
                 .about-us-section-7 { padding: 80px 0; background-color: #FFFFFF; }
                 .about-us-section-7-content { max-width: 1400px; margin: 0 auto; padding: 0 20px; }
                 .about-us-section-7-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px; }
                 .about-us-section-7-title { font-size: 2.5rem; font-weight: 700; font-family : Playfair Display ; color: var(--color--title, #131b5c); margin: 0; }
                 .blogs-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 40px; }
                 .blog-card { background: #fff; border-radius: 24px; overflow: hidden;  transition: all 0.3s ease; position: relative; }
                 .blog-card:hover { transform: translateY(-5px);  }
                 .blog-image { width: 100%; height: 250px; overflow: hidden; position: relative; }
                 .blog-image img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s ease; }
                 .blog-card:hover .blog-image img { transform: scale(1.05); }
                 .blog-category { position: absolute; top: 20px; left: 20px; background: rgba(255,255,255,0.9); padding: 8px 16px; border-radius: 20px; font-size: 0.9rem; font-weight: 600; color: #C37559; z-index: 1; }
                 .blog-content { padding: 32px 24px; position: relative; min-height: 400px; display: flex; flex-direction: column; }
                 .blog-title { font-size: 1.4rem; font-weight: 700; color: var(--color--title, #131b5c); margin: 0 0 16px 0; line-height: 1.3; }
                 .card-preview { color: #666; font-size: 1rem; line-height: 1.6; margin-bottom: 16px; height: 80px; overflow: hidden; }
                 .read-more-link { color: #C37559; text-decoration: none; font-weight: 600; font-size: 1rem; margin: 16px 0; transition: all 0.2s ease; display: inline-block; }
                 .read-more-link:hover { color: #B5674F; }
                 .card-meta { margin-top: auto; padding-top: 20px; border-top: 1px solid #eee; }
                 .blog-v2-feature-meta { display: flex; gap: 16px; color: #666; font-size: 0.9rem; }
                 .about-us-section-7-cta { display: flex; justify-content: center; width: 100%; margin-top: 60px; }
                 .about-us-section-7-btn { background: #C37559; color: white; border: none; padding: 16px 32px; border-radius: 50px; font-size: 1.1rem; font-weight: 600; cursor: pointer; transition: all 0.3s ease; }
                 .about-us-section-7-btn:hover { transform: translateY(-4px);  background: #B5674F; }
                 @media screen and (max-width: 991px) { .blogs-grid { grid-template-columns: repeat(2, 1fr); gap: 32px; } }
                 @media screen and (max-width: 767px) { .about-us-section-7 { padding: 60px 0; } .about-us-section-7-title { font-size: 2rem; } .blogs-grid { grid-template-columns: 1fr; gap: 24px; } .blog-content { min-height: 350px; } }
               `}</style>
               <div className="about-us-section-7" style={{ width: '100%' }}>
                 <div className="about-us-section-7-content">
                   <div className="about-us-section-7-header">
                     <h2 className="about-us-section-7-title">Discover Our Latest Blogs</h2>
                   </div>
                   <div className="blogs-grid">
                     {dummyBlogs
                       .sort((a, b) => new Date(b.date) - new Date(a.date))
                       .slice(0, 3)
                       .map((blog) => (
                         <div key={blog.id} className="blog-card" onClick={() => navigate(`/blog/${blog.id}`)} style={{ cursor: 'pointer' }}>
                           <div className="blog-image">
                             <img src={blog.blogimage} alt={blog.title} />
                           </div>
                           <div className="blog-category">{blog.category}</div>
                           <div className="blog-content">
                             <h3 className="blog-title">{blog.title}</h3>
                             <div className="card-preview">
                               <ReactMarkdown>{getFirstSentence(blog.content)}</ReactMarkdown>
                             </div>
                             <a
                               href="#"
                               className="read-more-link"
                               onClick={(e) => { e.preventDefault(); navigate(`/blog/${blog.id}`); }}
                             >
                               Read more
                             </a>
                             <div className="card-meta">
                               <div className="blog-v2-feature-meta">
                                 <span className="blog-v2-feature-author">{blog.author}</span>
                                 <span className="blog-v2-feature-date">{formatDate(blog.date)}</span>
                               </div>
                             </div>
                           </div>
                         </div>
                       ))}
                   </div>
                   <div className="about-us-section-7-cta">
                     <button className="about-us-section-7-btn" onClick={() => navigate('/blogs')}>See more</button>
                   </div>
                 </div>
               </div>
      </div>

      {/* Friendly Partner Notification Dialog */}
      <Dialog
        open={showPartnerDialog}
        onClose={handleDialogClose}
        aria-labelledby="partner-dialog-title"
        aria-describedby="partner-dialog-description"
        container={document.body}
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle id="partner-dialog-title" sx={{ color: '#1976d2' }}>
          Notice
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="partner-dialog-description">
            You are about to add a product from <strong>a different partner</strong>.
            <br /><br />
            Please note that your shopping cart now contains items from multiple suppliers.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDialogClose}
            color="primary"
            variant="outlined"
          >
            Understood
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Modal */}
      {showFilterModal && (
        <FilterModal
          onClose={() => setShowFilterModal(false)}
          onApplyFilters={handleApplyFilters}
          filters={filters}
        />
      )}
    </div>
  );
};

export default Category;
   